import type { RadioChangeEvent } from "antd"
import { Collapse, Radio, Select, Switch, Tooltip } from "antd"
import TextArea from "antd/es/input/TextArea"
import { useCallback } from "react"

import { ALL_LOCALES, type LocaleType } from "../../types/locale"
import SelectTags from "../SelectTags"
import type { Action } from "./reducer"
import type { AnyDocument } from "./state"

interface Props {
  className?: string
  documentIndex: number
  currentDocument: AnyDocument
  dispatch: React.Dispatch<Action>
  showCollapsible: boolean
}

const AdvancedMenuContent: React.FC<Omit<Props, "showCollapsible">> = ({
  currentDocument,
  documentIndex,
  dispatch,
}) => {
  const {
    verbatimFromAnswerBank,
    selectedTags,
    tagOperator,
    locale,
    customPrompt,
  } = currentDocument

  const onVerbatimFromAnswerBankChange = useCallback(
    (value: boolean) => {
      dispatch({
        type: "SET_VERBATIM_FROM_ANSWER_BANK",
        value,
      })
    },
    [dispatch],
  )

  const onSelectedTagsChange = useCallback(
    (payload: string[]) => {
      dispatch({
        type: "SET_SELECTED_TAGS",
        documentIndex,
        payload,
      })
    },
    [dispatch, documentIndex],
  )

  const onLocaleChange = useCallback(
    (payload: LocaleType) => {
      dispatch({
        type: "SET_LOCALE",
        documentIndex,
        payload,
      })
    },
    [dispatch, documentIndex],
  )

  const onTagOperatorChange = useCallback(
    (e: RadioChangeEvent) => {
      dispatch({
        type: "SET_TAG_OPERATOR",
        documentIndex,
        payload: e.target.value as "AND" | "OR",
      })
    },
    [dispatch, documentIndex],
  )

  const onCustomPromptChange = useCallback(
    (payload: string) => {
      dispatch({
        type: "SET_CUSTOM_PROMPT",
        documentIndex,
        payload,
      })
    },
    [dispatch, documentIndex],
  )

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Tooltip title="Only Answer Bank items that exactly match the questionnaire question will be used.">
          <label className="mr-2">
            Require answers to be verbatim from the Answer Bank:
          </label>
        </Tooltip>
        <Switch
          checked={verbatimFromAnswerBank}
          onChange={onVerbatimFromAnswerBankChange}
        />
      </div>
      <div className="flex gap-2">
        <label>Tag Filter:</label>
        <SelectTags
          className="max-w-[500px] grow"
          value={selectedTags}
          onChange={onSelectedTagsChange}
        />

        <Radio.Group onChange={onTagOperatorChange} value={tagOperator}>
          <Tooltip title="Only documents with ANY of these tags will be used">
            <Radio.Button value="OR">Any</Radio.Button>
          </Tooltip>
          <Tooltip title="Only documents with ALL of these tags will be used">
            <Radio.Button value="AND">All</Radio.Button>
          </Tooltip>
        </Radio.Group>
      </div>
      <div className="flex gap-2">
        <label>Language:</label>
        <Select
          className="min-w-32"
          onChange={onLocaleChange}
          value={locale}
          options={ALL_LOCALES}
        />
      </div>
      <div>
        <label className="mr-2">Custom Prompt:</label>
        <TextArea
          className="rounded border border-solid border-gray-200 p-1"
          value={customPrompt ?? ""}
          onChange={(e) => onCustomPromptChange(e.target.value)}
        />
      </div>
    </div>
  )
}

const AdvancedMenu: React.FC<Props> = ({
  className = "",
  documentIndex,
  currentDocument,
  dispatch,
  showCollapsible,
}) => {
  if (showCollapsible) {
    const collapseItems = [
      {
        label: "Advanced Options",
        key: "1",
        children: (
          <AdvancedMenuContent
            documentIndex={documentIndex}
            currentDocument={currentDocument}
            dispatch={dispatch}
          />
        ),
      },
    ]

    return <Collapse className={className} items={collapseItems} />
  }

  return (
    <div className={className}>
      <AdvancedMenuContent
        documentIndex={documentIndex}
        currentDocument={currentDocument}
        dispatch={dispatch}
      />
    </div>
  )
}

export default AdvancedMenu
